import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { ContactUploadBatchResponse } from "../../../services/generated/waba-api/models/contact-upload-batch-response";
import { ensureTranslateLangLoaded } from "../../../shared/utils/ensure-translate-lang";
import { translateContactUploadReason } from "../../../shared/utils/contact-upload-reason";

export interface ContactUploadBatchModalData {
  batchData: ContactUploadBatchResponse;
}

@Component({
  selector: "app-contact-upload-batch-modal",
  templateUrl: "./contact-upload-batch-modal.component.html",
  styleUrl: "./contact-upload-batch-modal.component.scss",
  standalone: true,
  imports: [CommonModule, TranslateModule, MatDialogModule],
})
export class ContactUploadBatchModalComponent implements OnInit, OnDestroy {
  downloading = false;
  isRTL = false;
  translationsReady = false;
  currentLang = "fr";
  private langChangeSubscription?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<ContactUploadBatchModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ContactUploadBatchModalData,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    ensureTranslateLangLoaded(this.translateService).subscribe((lang) => {
      this.currentLang = lang;
      this.updateDirection(lang);
      this.translationsReady = true;
      this.cdr.detectChanges();
    });

    this.langChangeSubscription = this.translateService.onLangChange.subscribe(
      (event) => {
        ensureTranslateLangLoaded(this.translateService, event.lang).subscribe(
          (lang) => {
            this.currentLang = lang;
            this.updateDirection(lang);
            this.cdr.detectChanges();
          },
        );
      },
    );
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
  }

  private updateDirection(lang: string): void {
    this.isRTL = lang === "ar";
  }

  get batchData(): ContactUploadBatchResponse {
    return this.data.batchData;
  }

  /** Rows the merchant still has to deal with: rejected while checking plus failed while saving. */
  get notImportedCount(): number {
    const listed = this.batchData.stagingFailureContacts?.length;
    if (listed != null) {
      return listed;
    }
    return (this.batchData.stagingFailureCount ?? 0) + (this.batchData.mainFailedCount ?? 0);
  }

  /** One plain sentence telling the merchant what happened and what to do next. */
  get guidanceKey(): string | null {
    const { status, errorCode } = this.batchData;
    if (status === "FAILED" && errorCode === "INTERRUPTED") {
      return "CONTACT_UPLOAD_BATCH.GUIDANCE.INTERRUPTED";
    }
    if (status === "FAILED") {
      return this.notImportedCount > 0
        ? "CONTACT_UPLOAD_BATCH.GUIDANCE.FAILED_ROWS"
        : "CONTACT_UPLOAD_BATCH.GUIDANCE.FAILED";
    }
    if (status === "PARTIAL") {
      return "CONTACT_UPLOAD_BATCH.GUIDANCE.PARTIAL";
    }
    if (status === "PROCESSING" || status === "PENDING") {
      return "CONTACT_UPLOAD_BATCH.GUIDANCE.RUNNING";
    }
    return null;
  }

  get statusConfig(): {
    class: string;
    icon: string;
    spinner?: boolean;
  } {
    const status = this.batchData.status;
    switch (status) {
      case "COMPLETED":
        return {
          class: "bg-green-100 text-green-700 border-green-200",
          icon: "fa-check-circle",
        };
      case "PARTIAL":
        return {
          class: "bg-orange-100 text-orange-700 border-orange-200",
          icon: "fa-exclamation-triangle",
        };
      case "FAILED":
        return {
          class: "bg-red-100 text-red-700 border-red-200",
          icon: "fa-times-circle",
        };
      case "PROCESSING":
        return {
          class: "bg-blue-100 text-blue-700 border-blue-200",
          icon: "",
          spinner: true,
        };
      case "PENDING":
      default:
        return {
          class: "bg-gray-100 text-gray-700 border-gray-200",
          icon: "fa-clock",
        };
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  downloadFailedContacts(): void {
    const failures = this.batchData.stagingFailureContacts;
    if (!failures || failures.length === 0) return;

    this.downloading = true;

    const headers = [
      this.translate("CONTACT_UPLOAD_BATCH.TABLE.PHONE_NUMBER"),
      this.translate("CONTACT_UPLOAD_BATCH.TABLE.NAME"),
      this.translate("CONTACT_UPLOAD_BATCH.TABLE.FIRST_NAME"),
      this.translate("CONTACT_UPLOAD_BATCH.TABLE.GENDER"),
      this.translate("CONTACT_UPLOAD_BATCH.TABLE.ERROR_REASON"),
    ].join(",");

    let csvContent = headers + "\n";
    failures.forEach((item) => {
      const contact = item.contact;
      csvContent += [
        this.escapeCSVField(contact?.phoneNumber || ""),
        this.escapeCSVField(contact?.name || ""),
        this.escapeCSVField(contact?.firstName || ""),
        this.escapeCSVField(contact?.gender || ""),
        this.escapeCSVField(this.translateReason(item.reason)),
      ].join(",") + "\n";
    });

    const bom = "\uFEFF";
    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `failed-contacts-${this.batchData.batchReference || "export"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    this.downloading = false;
  }

  private escapeCSVField(value: string): string {
    const val = (value || "").replace(/"/g, '""');
    return `"${val}"`;
  }

  translateReason(reason: string | undefined | null): string {
    return translateContactUploadReason(reason, this.translateService);
  }

  private translate(key: string): string {
    return this.translateService.instant(key);
  }
}
